// Application Components
import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { object, string } from 'yup'
import { translateField } from '../../functions/field-functions'
import { extractSubmissionForm } from '../../functions/form-functions'

//// Application Slices
import { selectSettings } from '../../reducers/settingSlice'
import {
    updateFormData,
    updateFormValidity,
    updateFormSystem,
    selectForms,
} from '../../reducers/formSlice'

//// Application Components
import ButtonComponent from '../../components/conditional-components/button-component'
import LoadingComponent from '../../components/simple-components/loading-component'
import NotificationComponent from '../../components/conditional-components/notification-component'

//// Carbon Components
import { Form, RadioButtonGroup, RadioButton } from '@carbon/react'

//// Carbon Icons

//// Static Constants

//////// Component
const RegisteredNursingExaminationFloridaCenterForNursingForm = ({
    instance,
}) => {
    //////// Hooks
    const dispatch = useDispatch()
    const navigate = useNavigate()

    //////// State
    const [loading, setLoading] = useState(false)
    const [notification, setNotification] = useState({})

    //////// Slices
    const settingsData = useSelector(selectSettings)
    const formsData = useSelector(selectForms)

    //// Slice Data
    const digital_name = settingsData?.['form']?.['digital_name']
    const submit_route = settingsData?.['form']?.['submit_route']
    const dynamic_submit_route1 =
        settingsData?.['form']?.['dynamic_submit_route1']
    const dynamic_submit_route2 =
        settingsData?.['form']?.['dynamic_submit_route2']
    const fields = settingsData?.['form']?.['fields']

    //////// Search Params

    //////// Methods

    // Validate Data Submission
    const validation = async (submit) => {
        // Validate Form Submission Data Against Schema
        try {
            // Schema Object
            const schema = {}
            schema.yup = object({
                FL_CND_Q: string().required(
                    'This field is required to be entered or selected.'
                ),
            })
            schema.valid = {
                FL_CND_Q: false,
                FL_CND_QErrMsg: '',
            }
            await schema.yup.validate(
                { ...submit },
                { strict: true, abortEarly: false }
            )
            dispatch(
                updateFormValidity({
                    digital_name: digital_name,
                    errors: {},
                })
            )
            return true
        } catch (err) {
            const errors = {}
            if (err?.inner?.length > 0) {
                await err.inner.forEach(async (el) => {
                    if (el?.path) {
                        errors[el?.path] = true
                        if (el?.errors?.length > 0) {
                            const errName = el?.path + 'ErrMsg'
                            const errMsg = el?.errors?.join(' ')
                            const tran = translateField(fields, el?.path)
                            if (tran) {
                                const tranErrMsg = await errMsg.replaceAll(
                                    el?.path,
                                    tran
                                )
                                errors[errName] = tranErrMsg
                            } else {
                                errors[errName] = el?.errors?.join(' ')
                            }
                        }
                    }
                })
            }
            //// Start Watson Assistant Journey

            //// End Watson Assistant Journey
            dispatch(
                updateFormValidity({
                    digital_name: digital_name,
                    errors: errors,
                })
            )
            return false
        }
    }

    // Re-Validate Data Submission
    const revalidation = async (event_name) => {
        try {
            // Update Validation State
            if (digital_name && event_name) {
                dispatch(
                    updateFormValidity({
                        digital_name: digital_name,
                        errors: {
                            [event_name]: false,
                            [event_name + 'ErrMsg']: '',
                        },
                    })
                )
            }
        } catch (error) {
            console.warn('Revalidation Error:', error)
            return null
        }
    }

    //////// Functional Methods

    // Handle Data Of All Fields
    const handleData = (e) => {
        if (e?.target?.name && e?.target?.value) {
            dispatch(
                updateFormData({
                    digital_name: digital_name,
                    values: {
                        [e.target.name]: e.target.value,
                    },
                })
            )
            revalidation(e.target.name)
        } else if (e?.target?.name) {
            dispatch(
                updateFormData({
                    digital_name: digital_name,
                    values: { [e.target.name]: '' },
                })
            )
            revalidation(e.target.name)
        }
    }

    // Handle Form Submission
    const handleSubmit = async () => {
        try {
            console.info('Florida Center For Nursing: Submitted')

            setLoading(true)

            // Extract Form Data From Redux
            const submit = await extractSubmissionForm(digital_name, formsData)

            // Validate Form Submission Data
            const valid = await validation(submit)

            // Check If Form Data Is Valid
            if (
                valid &&
                formsData?.['get-started']?.['values']?.['GetStar'] === 'Y'
            ) {
                console.info('Florida Center For Nursing: Passed Valid')

                dispatch(
                    updateFormSystem({
                        digital_name: digital_name,
                        system: { valid: true },
                    })
                )
                setLoading(false)
                setNotification({
                    type: 'inline',
                    visible: false,
                    kind: '',
                    title: '',
                    subtitle: '',
                })

                // Navigate To Next Form
                navigate(dynamic_submit_route1 ? dynamic_submit_route1 : '/')
            } else if (
                valid &&
                formsData?.['get-started']?.['values']?.['GetStar'] === 'N' &&
                formsData?.['get-started']?.['values']?.['GetStarTwo'] === 'Y'
            ) {
                console.info('Florida Center For Nursing: Passed Valid')

                dispatch(
                    updateFormSystem({
                        digital_name: digital_name,
                        system: { valid: true },
                    })
                )
                setLoading(false)
                setNotification({
                    type: 'inline',
                    visible: false,
                    kind: '',
                    title: '',
                    subtitle: '',
                })

                // Navigate To Next Form
                navigate(dynamic_submit_route2 ? dynamic_submit_route2 : '/')
            } else if (valid) {
                console.info('Florida Center For Nursing: Passed Valid')

                dispatch(
                    updateFormSystem({
                        digital_name: digital_name,
                        system: { valid: true },
                    })
                )
                setLoading(false)
                setNotification({
                    type: 'inline',
                    visible: false,
                    kind: '',
                    title: '',
                    subtitle: '',
                })

                // Navigate To Next Form
                navigate(submit_route ? submit_route : '/')
            } else {
                console.info('Florida Center For Nursing: Failed Valid')

                dispatch(
                    updateFormSystem({
                        digital_name: digital_name,
                        system: { valid: false },
                    })
                )
                setLoading(false)
                setNotification({
                    type: 'inline',
                    visible: true,
                    kind: 'error',
                    title: 'Form Errors:',
                    subtitle:
                        'There are errors on the form (such as missing required info). Please scroll up and update the fields with red messages.',
                })

                console.log(
                    'Florida Center For Nursing Failed Valid: There are errors on the form (such as missing required info). Please scroll up and update the fields with red messages.'
                )
            }
        } catch (error) {
            console.warn('Handle Submit Error:', error)
            return null
        }
    }

    // Handle Keyboard Enter As Submit (Method)
    const handleKeyDown = async (event) => {
        try {
            if (event?.key === 'Enter') {
                await handleSubmit()
            }
        } catch (error) {
            console.warn('Handle Down Key Error:', error)
            return null
        }
    }

    //////// Memoization

    //////// Effects/Side Effects

    // Reload Watson Assistant Journey on Reload
    useEffect(() => {
        if (instance?.tours) {
            instance.tours.endTour()
            instance.tours.startTour(
                'Registered Nursing Endorsement Florida Center For Nursing Tour'
            )
        }
        return () => {}
    }, [instance])

    //////////////// Render & Return
    if (loading) {
        return (
            <>
                <Form
                    tabIndex={0}
                    onKeyDown={handleKeyDown}
                    onSubmit={handleSubmit}
                >
                    <LoadingComponent />
                </Form>
            </>
        )
    } else {
        return (
            <>
                <Form
                    tabIndex={0}
                    onKeyDown={handleKeyDown}
                    onSubmit={handleSubmit}
                >
                    <p>
                        The Florida Center for Nursing is the definitive source
                        for information, research, and strategies addressing the
                        dynamic nurse workforce needs in Florida. The Center
                        conducts multiple annual and biennial research projects,
                        including nurse employer and nursing program surveys, to
                        provide a comprehensive look at {"Florida's"} nurse
                        population.
                    </p>

                    <p>
                        Based on this research, the Center projects a severe
                        nursing shortage in Florida - a shortage that could have
                        a devastating impact on health care quality and access
                        for {"Florida's"} residents. The Florida Center for
                        Nursing also uses the research it produces to address
                        the issues of supply and demand and utilization of
                        scarce nurse workforce resources throughout the state.
                    </p>

                    <p>
                        In addition to nurse workforce research, the Florida
                        Center for Nursing aims to improve retention and
                        recruitment of nurses in Florida through funding small
                        grants and also by collecting and disseminating
                        information on best practices and innovative strategies
                        for nurse retention and recruitment. Increasing
                        production of new nurses alone will not resolve the
                        shortage. Efforts must be taken to retain the
                        experiential knowledge of our existing nurses.
                    </p>

                    <p>
                        The Florida Center for {"Nursing's"} operating revenues
                        are derived in part from your donation. In order for the
                        Florida Center for Nursing to continue its work on
                        behalf of nurses, please consider donating.
                    </p>

                    <RadioButtonGroup
                        className={
                            formsData?.[digital_name]?.errors?.FL_CND_Q
                                ? 'required invalid'
                                : 'required'
                        }
                        defaultSelected={
                            formsData?.[digital_name]?.values?.FL_CND_Q
                        }
                        valueSelected={
                            formsData?.[digital_name]?.values?.FL_CND_Q
                        }
                        legendText="Do you want to donate to the Florida Center for Nursing?"
                        name="FL_CND_Q"
                        invalid={formsData?.[digital_name]?.errors?.FL_CND_Q}
                        invalidText={
                            formsData?.[digital_name]?.errors?.FL_CND_QErrMsg
                        }
                    >
                        <RadioButton
                            id="FL_CND_QTrue"
                            name="FL_CND_QTrue"
                            labelText="Yes"
                            value="Y"
                            type="radio"
                            onClick={handleData}
                        />
                        <RadioButton
                            id="FL_CND_QFalse"
                            name="FL_CND_QFalse"
                            labelText="No"
                            value="N"
                            type="radio"
                            onClick={handleData}
                        />
                    </RadioButtonGroup>

                    <p>
                        <strong>If {`"Yes"`}</strong> thank you for indicating
                        your desire to donate to the Florida Center for Nursing.
                        Please submit your donation to the address below:
                    </p>

                    <p className="fldoh-address-box">
                        Department of Health
                        <br />
                        Post Office Box 6320
                        <br />
                        Tallahassee, Florida 32314-6320
                    </p>

                    <p>
                        Donations are voluntary and do not impact the processing
                        of your application. Donations made through the Florida
                        Center for {`Nursing's`} website are tax deductible.
                    </p>

                    <NotificationComponent notification={notification} />

                    <div className="button-group">
                        <div className="button-group-item">
                            <ButtonComponent
                                click={handleSubmit}
                                title="Save Progress"
                            />
                        </div>
                    </div>
                </Form>
            </>
        )
    }
}

export default RegisteredNursingExaminationFloridaCenterForNursingForm
